import type { AutosaveState } from '../../features/forms/index.js';

export interface OnboardingAutosaveBadgeProps {
  state: AutosaveState;
  error: Error | null;
}

/**
 * Badge discreto que mostra o estado do auto-save do questionário.
 * Sempre visível em uma linha logo abaixo do cabeçalho — dá ao lead
 * sinal constante de que o trabalho dele **não será perdido**.
 *
 * **Decisão de UX**: mostrar "Rascunho salvo" durante a maior parte
 * do tempo aumenta confiança; mostrar "Salvando…" efêmero por 200ms
 * só pisca. Por isso os textos são estáveis e curtos, e o estado
 * `idle` mostra "Rascunho salvo" (último status conhecido).
 *
 * Tokens do DS: `text-muted-foreground` para estados neutros,
 * `text-primary` para saving (cor da marca), `text-destructive` para
 * erro. Zero hardcode.
 */
export function OnboardingAutosaveBadge({ state, error }: OnboardingAutosaveBadgeProps) {
  if (state === 'error') {
    return (
      <p className="px-1 text-xs text-destructive" role="status">
        ⚠ Não foi possível salvar. Suas respostas continuam aqui — tente digitar algo para salvar
        novamente.
        {error?.message ? <span className="italic"> ({error.message})</span> : null}
      </p>
    );
  }

  const text = getBadgeText(state);
  return (
    <p
      className={`px-1 text-xs ${state === 'saving' ? 'text-primary' : 'text-muted-foreground'}`}
      role="status"
      aria-live="polite"
    >
      {text}
    </p>
  );
}

function getBadgeText(state: AutosaveState): string {
  switch (state) {
    case 'idle':
    case 'saved':
      return 'Rascunho salvo automaticamente.';
    case 'pending':
      return 'Aguardando para salvar…';
    case 'saving':
      return 'Salvando…';
    case 'error':
      return 'Erro ao salvar.';
  }
}
